import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  Menu,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { AppstoreOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../api/axios';
import '../styles/Settings.css';

const { Title, Text } = Typography;

const typeOptions = [
  { key: 'email_type', label: '電郵類型' },
  { key: 'address_type', label: '地址類型' },
  { key: 'phone_type', label: '電話類型' },
  { key: 'language_type', label: '語言類型' },
  { key: 'role_type', label: '角色類型' },
];

function Settings() {
  const location = useLocation();
  const enterpriseId = location.pathname.match(/^\/enterprise\/([^/]+)/)?.[1];
  const [selectedType, setSelectedType] = useState(typeOptions[0].key);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const selectedOption = typeOptions.find((option) => option.key === selectedType);

  const loadCategories = useCallback(async () => {
    if (!enterpriseId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/customer/GetContactTypeCategories', {
        params: { cust_uuid: enterpriseId, type_code: selectedType },
      });
      setCategories(Array.isArray(data?.categories) ? data.categories : []);
    } catch (error) {
      message.error(error.response?.data?.message || '無法載入類型記錄');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, selectedType]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        cust_uuid: enterpriseId,
        type_description: values.type_description.trim(),
        remark: values.remark?.trim() || '',
        is_active: values.is_active,
        is_default: values.is_default,
      };
      if (editingRecord) {
        await api.put('/customer/UpdateContactTypeCategory', { ...payload, uuid: editingRecord.uuid });
      } else {
        await api.post('/customer/AddContactTypeCategory', { ...payload, type_code: selectedType });
      }
      message.success(`已${editingRecord ? '更新' : '新增'}${selectedOption.label}`);
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      await loadCategories();
    } catch (error) {
      message.error(error.response?.data?.message || '新增記錄失敗');
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.setFieldsValue({ type_description: '', remark: '', is_active: true, is_default: false });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      type_description: record.type_description,
      remark: record.remark || '',
      is_active: record.is_active,
      is_default: record.is_default,
    });
    setModalOpen(true);
  };

  const columns = useMemo(() => [
    { title: '類型名稱', dataIndex: 'type_description', key: 'type_description' },
    {
      title: '狀態', dataIndex: 'is_active', key: 'is_active', width: 100,
      render: (active) => <Tag color={active ? 'green' : 'default'}>{active ? '啟用' : '停用'}</Tag>,
    },
    {
      title: '預設', dataIndex: 'is_default', key: 'is_default', width: 90,
      render: (isDefault) => isDefault ? <Tag color="blue">預設</Tag> : '—',
    },
    { title: '備註', dataIndex: 'remark', key: 'remark', render: (remark) => remark || '—' },
    {
      title: '更新時間', dataIndex: 'updated_at', key: 'updated_at', width: 180,
      render: (value) => value ? new Date(value).toLocaleString('zh-TW') : '—',
    },
    {
      title: '操作', key: 'actions', width: 90, fixed: 'right',
      render: (_, record) => <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(record)}>編輯</Button>,
    },
  // openEditModal only writes the selected row into the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const menuItems = [{
    key: 'type-management',
    icon: <AppstoreOutlined />,
    label: '類型管理',
    children: typeOptions,
  }];

  return (
    <div className="settings-page">
      <aside className="settings-sidebar">
        <Menu mode="inline" selectedKeys={[selectedType]} defaultOpenKeys={['type-management']} items={menuItems} onClick={({ key }) => {
          if (typeOptions.some((option) => option.key === key)) setSelectedType(key);
        }} />
      </aside>

      <main className="settings-main">
        <div className="settings-heading">
          <div>
            <Title level={3}>{selectedOption.label}</Title>
            <Text type="secondary">管理目前公司的{selectedOption.label}記錄。</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} disabled={!enterpriseId} onClick={openCreateModal}>新增記錄</Button>
        </div>

        <Card className="settings-table-card">
          {enterpriseId ? (
            <Table rowKey="uuid" columns={columns} dataSource={categories} loading={loading} pagination={{ pageSize: 10, showSizeChanger: false }} locale={{ emptyText: <Empty description={`尚未建立${selectedOption.label}`} /> }} />
          ) : <Empty description="請先選擇公司" />}
        </Card>
      </main>

      <Modal title={`${editingRecord ? '編輯' : '新增'}${selectedOption.label}`} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()} confirmLoading={saving} okText={editingRecord ? '儲存' : '新增'} cancelText="取消" destroyOnHidden>
        <Form form={form} layout="vertical" initialValues={{ is_active: true, is_default: false }} onFinish={handleSave}>
          <Form.Item label="類型名稱" name="type_description" rules={[{ required: true, whitespace: true, message: '請輸入類型名稱' }, { max: 255, message: '不可超過 255 個字元' }]}>
            <Input placeholder={`例如：${selectedOption.label.replace('類型', '')}`} maxLength={255} showCount />
          </Form.Item>
          <Form.Item label="備註" name="remark" rules={[{ max: 255, message: '不可超過 255 個字元' }]}>
            <Input.TextArea rows={3} maxLength={255} showCount />
          </Form.Item>
          <Space size="large">
            <Form.Item label="啟用" name="is_active" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item label="設為預設" name="is_default" valuePropName="checked"><Switch /></Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

export default Settings;
